// SearchBar component for UI Component Library
// Created as part of ENG2-1691: Phase 3 - Ticket 3.3

import React from 'react'
import { Box, InputBase, useTheme } from '@mui/material'
import { styled } from '@mui/material/styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { tokens } from '../../../styles/tokens'
import { SearchBarProps } from './search-bar.types'

// Styled search container
const SearchContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'searchWidth',
})<{ searchWidth?: number | string }>(({ theme, searchWidth }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  height: 38,
  padding: '0 12px',
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : tokens.colors.grey[50],
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: tokens.radius.sm,
  transition: `all ${tokens.transitions.fast}`,
  width: searchWidth || 280,

  '&:focus-within': {
    backgroundColor: theme.palette.background.paper,
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 3px ${theme.palette.mode === 'dark' ? 'rgba(255, 192, 0, 0.15)' : tokens.colors.primary[50]}`,
  },
}))

// Styled input
const StyledInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  fontSize: 13,
  color: theme.palette.text.primary,

  '& input': {
    padding: 0,

    '&::placeholder': {
      color: theme.palette.text.disabled,
      opacity: 1,
    },
  },
}))

export const SearchBar: React.FC<SearchBarProps> = ({
  placeholder = 'Search...',
  value,
  onChange,
  onSubmit,
  width,
  autoFocus = false,
  sx,
  className,
  'data-cy': dataCy,
}) => {
  const theme = useTheme()

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onSubmit) {
      onSubmit()
    }
  }

  return (
    <SearchContainer searchWidth={width} sx={sx} className={className}>
      <FontAwesomeIcon
        icon={faSearch}
        style={{
          color: theme.palette.text.disabled,
          fontSize: 14,
        }}
      />
      <StyledInput
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        autoFocus={autoFocus}
        inputProps={{
          'data-cy': dataCy,
          'aria-label': placeholder,
        }}
      />
    </SearchContainer>
  )
}

SearchBar.displayName = 'SearchBar'

export default SearchBar
export type { SearchBarProps } from './search-bar.types'
